<template>
  <ui-modal-layout :title="title" :icon="BugIcon">
    <div class="flex items-center gap-2.5">
      <ui-select
        :model-value="selectedMethod?.method ?? null"
        :disabled="availableMethods.length === 0"
        @update:model-value="(value) => selectedMethod = availableMethods.find(item => item.method === value) ?? null"
      >
        <ui-select-trigger class="min-w-0 flex-1" :aria-label="tr('Method')">
          <span class="flex min-w-0 items-center gap-2">
            <loader-circle-icon v-if="isLoading || availableMethods.length === 0" class="size-4 shrink-0 animate-spin text-primary" />
            <circle-check-icon v-else-if="response?.success" class="size-4 shrink-0 text-success" />
            <circle-x-icon v-else-if="response" class="size-4 shrink-0 text-destructive" />
            <span class="truncate">{{ selectedMethod?.label }}</span>
          </span>
        </ui-select-trigger>
        <ui-select-content>
          <ui-select-item v-for="method in availableMethods" :key="method.method" :value="method.method">
            {{ method.label }}
          </ui-select-item>
        </ui-select-content>
      </ui-select>

      <ui-button :disabled="isLoading || selectedMethod === null" @click="() => sendCommunicatorRequest()">
        <send-icon />
        {{ tr('Send') }}
      </ui-button>
    </div>

    <div v-if="selectedMethod !== null" class="flex gap-5 max-md:flex-col">
      <div class="flex shrink-0 flex-col gap-3 md:w-56">
        <p v-if="Object.keys(selectedMethod.params).length === 0" class="m-0 py-5 text-center text-muted-foreground">
          {{ tr('No params for this method') }}
        </p>
        <template v-else>
          <div v-for="(param, key) in selectedMethod.params" :key="key" class="flex flex-col gap-1.5">
            <label v-if="param.type === 'bool'" class="flex cursor-pointer items-center gap-2 text-sm text-foreground">
              <ui-checkbox
                :model-value="!!param.value"
                :disabled="isLoading"
                @update:model-value="(value) => param.value = value"
              />
              {{ key }}{{ param.default ? '' : '*' }}
            </label>

            <template v-else>
              <span class="text-sm font-medium text-foreground">{{ key }}{{ param.default ? '' : '*' }}</span>
              <ui-input
                v-model="param.value"
                :type="param.type === 'int' ? 'number' : 'text'"
                :placeholder="param.default ? 'Default: ' + param.default : ''"
                :disabled="isLoading"
              />
            </template>
          </div>
        </template>

        <div v-if="showAccessTokenButton && $root.supportHasL3Access" class="mt-auto flex flex-col gap-1.5 border-t border-border pt-3">
          <span class="text-center text-xs text-muted-foreground">{{ accessTokenEntityName }}</span>

          <div v-if="accessToken" class="flex items-center gap-1.5">
            <ui-input
              ref="accessTokenInput"
              :model-value="accessToken"
              readonly
              class="h-8 text-xs"
              @click="() => selectAccessTokenInputContent()"
            />
            <ui-button
              v-tooltip="showCheckmark ? tr('Copied') : tr('Copy to clipboard')"
              variant="ghost"
              size="icon"
              :aria-label="tr('Copy to clipboard')"
              @click="() => copyToClipboard(accessToken)"
            >
              <check-icon v-if="showCheckmark" class="text-success" />
              <copy-icon v-else />
            </ui-button>
          </div>
          <ui-button
            v-else
            size="sm"
            :disabled="isAccessTokenLoading"
            @click="() => sendAccessTokenRequest()"
          >
            <loader-circle-icon v-if="isAccessTokenLoading" class="animate-spin" />
            <key-round-icon v-else />
            {{ tr('Get Access Token') }}
          </ui-button>
          <ui-button variant="outline" size="sm" @click="() => openTokenRequestLog()">
            <notebook-text-icon />
            {{ tr('Token Request Log') }}
          </ui-button>
        </div>
      </div>

      <div class="glass-input max-h-[60vh] min-h-64 min-w-0 flex-1 overflow-auto rounded-xl py-2.5 text-xs [&_.vjs-tree-node]:px-4">
        <vue-json-pretty
          v-if="response !== null"
          :data="response.error ?? response.response"
          :deep="3"
          show-line-number
          show-icon
        />
      </div>
    </div>
  </ui-modal-layout>
</template>

<script>
import { Bug, Check, CircleCheck, CircleX, Copy, KeyRound, LoaderCircle, NotebookText, Send } from '@lucide/vue';
import { markRaw } from 'vue';
import VueJsonPretty from 'vue-json-pretty';

import AdAccount from '@/entities/AdAccount';
import ConnectedProfile from '@/entities/ConnectedProfile';
import Post from '@/entities/Post.js';
import SourceProfile from '@/entities/SourceProfile.js';
import SupportService from '@/services/Support.js';
import TokenRequestLogModal from '@/ui/components/TokenRequestLogModal.vue';
import { ucfirst } from '@/utils/string';

export default {
  name: 'CommunicatorDebug',
  components: {
    CheckIcon: Check,
    CircleCheckIcon: CircleCheck,
    CircleXIcon: CircleX,
    CopyIcon: Copy,
    KeyRoundIcon: KeyRound,
    LoaderCircleIcon: LoaderCircle,
    NotebookTextIcon: NotebookText,
    SendIcon: Send,
    VueJsonPretty,
  },
  props: {
    entity: {
      type: [ SourceProfile, AdAccount, ConnectedProfile, Post ],
      required: true,
    },
  },
  data() {
    return {
      BugIcon: markRaw(Bug),
      availableMethods: [],
      selectedMethod: null,
      isLoading: false,
      isAccessTokenLoading: false,
      response: null,
      accessToken: null,
      showCheckmark: false,
    };
  },
  computed: {
    entityType() {
      if (this.entity instanceof SourceProfile) {
        return 'source_profile';
      }
      if (this.entity instanceof AdAccount) {
        return 'ad_account';
      }
      if (this.entity instanceof ConnectedProfile) {
        return 'connected_profile';
      }
      if (this.entity instanceof Post) {
        return 'post';
      }
      throw new Error('Invalid entity type');
    },
    title() {
      return (this.entity.name || this.entity.external_id) + ' ' + this.tr('debug');
    },
    buildInitEntity() {
      if (this.entity instanceof Post) {
        return {
          entityType: 'source_profile',
          entityId: this.entity.source_profile.id,
        };
      }

      return {
        entityType: this.entityType,
        entityId: this.entity.id,
      };
    },
    showAccessTokenButton() {
      return this.entity instanceof ConnectedProfile
        || this.entity instanceof SourceProfile;
    },
    accessTokenEntityName() {
      let entityType = '';
      if (this.entity instanceof ConnectedProfile) {
        entityType = this.tr('Connected Profile');
      }
      if (this.entity instanceof SourceProfile) {
        entityType = this.tr('Source Profile');
      }

      return ucfirst(this.entity.platform) + ' ' + entityType;
    },
  },
  watch: {
    selectedMethod() {
      this.response = null;

      if (!this.selectedMethod) {
        return;
      }

      for (const paramKey in this.selectedMethod.params) {
        const param = this.selectedMethod.params[paramKey];
        if (param.default) {
          param.value = param.default;
        }
        else {
          param.value = null;
        }
      }
    },
  },
  created() {
    this.fetchAvailableMethods();
  },
  methods: {
    fetchAvailableMethods() {
      SupportService.getCommunicatorMethods(this.entity.platform, this.buildInitEntity.entityType, this.buildInitEntity.entityId)
        .then(resp => {
          this.availableMethods = resp;
          this.selectedMethod = this.availableMethods[0];

          return resp;
        })
        .catch(error => this.error(error));
    },
    sendAccessTokenRequest() {
      if (this.isAccessTokenLoading || !this.$root.supportHasL2Access) {
        return;
      }

      this.isAccessTokenLoading = true;

      SupportService.getAccessToken(this.buildInitEntity.entityType, this.buildInitEntity.entityId)
        .then(resp => this.accessToken = resp.access_token)
        .catch(error => this.error(error))
        .finally(() => this.isAccessTokenLoading = false);
    },
    sendCommunicatorRequest() {
      if (this.isLoading) {
        return;
      }

      if (!this.$root.supportHasL2Access) {
        this.$toast.error('Not enough support level');
        return;
      }

      const { method } = this.selectedMethod;
      const { params } = this.selectedMethod;

      for (const paramKey in params) {
        const param = params[paramKey];
        if (param.required && !param.value) {
          this.error(this.tr(':field is required!', { ':field': paramKey }));
          return;
        }
      }

      this.isLoading = true;

      const paramsObj = {};
      for (const key in params) {
        paramsObj[key] = params[key].value;
      }

      SupportService.getCommunicatorResponse(
        this.entity.platform,
        this.buildInitEntity.entityType,
        this.buildInitEntity.entityId,
        method,
        paramsObj,
      )
        .then(resp => this.response = resp)
        .catch(error => this.error(error))
        .finally(() => this.isLoading = false);
    },
    selectAccessTokenInputContent() {
      this.$refs.accessTokenInput.$el.select();
    },
    async copyToClipboard(text) {
      this.selectAccessTokenInputContent();

      try {
        await navigator.clipboard.writeText(text);
        this.showCheckmark = true;
        setTimeout(() => {
          this.showCheckmark = false;
        }, 2000);
      }
      catch (err) {
        this.error(this.tr('Failed to copy'));
      }
    },
    openTokenRequestLog() {
      this.$fullscreenModal.open(
        this,
        TokenRequestLogModal,
        {
          entityType: this.buildInitEntity.entityType,
          entityId: this.buildInitEntity.entityId,
          name: this.entity.name,
        },
      );
    },
  },
};
</script>
